import React, { useState } from "react";
import { Button, Divider, Image, Input } from "@heroui/react";
import { Link } from "@remix-run/react";
import { MdEmail, MdOutlineLocalPhone } from "react-icons/md";
import logo from "app/assets/logo.png";
import { FooterLinkComp } from "./FooterLinkComp";
import { aboutLinks, companyInfo, faqLinks, otherLinks, socialLinks } from "./links";
import ContactDetails from "./ContactDetails";
import { SubscribeToNewsLetter } from "app/api/contactUs.api";
import { toast, ToastContainer } from "react-toastify";
import { useAtom } from "jotai";
import { categoryList } from "app/atoms/category.atom";
import { toKebabCase } from "app/utils/fn";

export function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [categories] = useAtom(categoryList);

  const validateEmail = (value: string) => {
    const trimmed = value.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(trimmed);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!email.trim()) {
      setError("Email is required");
      return;
    }

    if (!validateEmail(email)) {
      setError("Invalid email address");
      return;
    }

    setLoading(true);
    try {
      await SubscribeToNewsLetter(email.trim());
      toast.success("You have successfully subscribed to our newsletter");
      setEmail("");
    } catch (error: any) {
      const errorMessage = Array.isArray(error?.response?.data?.message)
        ? error.response.data.message.join(", ") // Convert array to a readable string
        : error?.response?.data?.message || "Subscription failed. Please try again.";
      // You can add custom error handling logic here.
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (email) {
      if (validateEmail(email)) {
        setError("");
        return;
      }
    }
  }, [email]);

  // Map categories to FooterT[]
  const categoryFooterLinks = (categories || []).map((cat) => ({
    name: cat.name,
    pathname: `/category/${toKebabCase(cat.name)}`,
  }));

  return (
    <footer className="bg-gray-50 dark:bg-gray-800 border-t-1 border-neutral-300 dark:border-gray-700">
      <ToastContainer />
      <div className="py-6">
        <div className="container mx-auto lg:w-11/12 px-3 md:px-6 lg:px-12 xl:px-12 lg:py-8 space-y-6 md:space-y-10">
          {/* Newsletter Section */}
          <div className="sm:flex sm:items-center sm:justify-between space-y-6 sm:space-y-0 w-full">
            <div>
              <h2 className="text-lg md:text-3xl text-foreground font-semibold">Newsletter</h2>
              <p className="text-sm text-foreground/80 mt-1">
                Get exclusive offers, new product alerts and promo tips once a month.
              </p>
            </div>
            <div className="flex sm:justify-end w-full">
              <form onSubmit={handleSubmit} className="w-full sm:w-3/4">
                <div className="flex flex-col gap-2 w-full">
                  <div className="flex items-center">
                    <Input
                      size="sm"
                      radius="none"
                      variant="bordered"
                      placeholder="Your email address"
                      startContent={<MdEmail className="text-xl" aria-label="Email Icon" />}
                      aria-label="Enter your email to subscribe to the newsletter"
                      classNames={{
                        inputWrapper: ["border", "border-zinc-400", "h-12"],
                        mainWrapper: "rounded-md",
                      }}
                      className="rounded-s-[4px] rounded-e-none bg-transparent w-full overflow-hidden"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      isInvalid={!!error}
                    />
                    <Button
                      type="submit"
                      size="lg"
                      variant="ghost"
                      isLoading={loading}
                      isDisabled={loading}
                      className="border-none rounded-s-none rounded-e-[4px] bg-primary text-white font-semibold"
                      aria-label="Subscribe and get offers"
                    >
                      Get offers
                    </Button>
                  </div>
                  {error && (
                    <p className="text-red-500 text-sm" aria-live="assertive">
                      {error}
                    </p>
                  )}
                </div>
              </form>
            </div>
          </div>

          {/* Trust row + primary CTA */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 py-4 border-y border-gray-200 dark:border-gray-600">
            <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6 text-xs sm:text-sm text-foreground/80">
              <span className="font-medium">Price guarantee</span>
              <span className="text-gray-400 dark:text-gray-500" aria-hidden>•</span>
              <span className="font-medium">Fast delivery Australia wide</span>
              <span className="text-gray-400 dark:text-gray-500" aria-hidden>•</span>
              <span className="font-medium">Secure checkout</span>
            </div>
            <div className="flex items-center gap-3 shrink-0">

              <Button
                as={Link}
                to="tel:1300664999"
                size="md"
                variant="bordered"
                startContent={<MdOutlineLocalPhone className="text-lg" />}
                className="border-primary text-primary font-medium rounded-lg"
                aria-label="Call 1300 664999 for sales"
              >
                1300 664999
              </Button>
            </div>
          </div>

          {/* Main Footer Content */}
          <div className="grid gap-8 sm:gap-6 sm:grid-cols-4">
            <div className="mb-6 md:mb-0 space-y-4">
              <Link
                to="/"
                prefetch="intent"
                className="flex items-center mx-auto md:mx-0 w-fit"
                aria-label="Go to homepage"
              >
                <Image
                  src={logo}
                  loading="lazy"
                  width={249}
                  height={60}
                  removeWrapper
                  // className="h-10 md:h-12 lg:h-14 2xl:h-16 w-auto"
                  style={{ aspectRatio: "4.15/1" }}
                  alt="Promotional Products Now Logo"
                />
              </Link>
              <div>
                <div className="space-y-3">
                  <p className="text-xs font-normal text-gray dark:text-gray-100">
                    Promotional Products Now is a progressive promotional products company founded
                    on the belief that customer satisfaction is of paramount and continuing
                    importance.
                  </p>
                  <p className="text-xs font-normal text-gray dark:text-gray-100">
                    Serving all organizations around Australia for over 20 years, our success to
                    date is directly contributed to the feedback received from customers.
                  </p>
                </div>
              </div>
              <div className="hidden md:block">
                <div className="space-y-4">
                  <h3 className="text-lg text-foreground font-semibold">Follow Us</h3>
                  <div className="flex items-center space-x-3">
                    {socialLinks.map((link) => {
                      const IconTag = link.icon;
                      return (
                        <Link
                          to={link.pathname}
                          key={link.id}
                          target="_blank"
                          aria-label={`Follow us on ${link.id}`}
                        >
                          <IconTag
                            className="text-primary text-2xl"
                            aria-label={`${link.id} Icon`}
                          />
                        </Link>
                      );
                    })}
                  </div>
                </div>
                <p className="text-xs font-normal text-gray dark:text-gray-100">
                  Copyright {new Date().getFullYear()} Promotional Products Now
                </p>
              </div>
            </div>

            <div className="hidden md:contents w-full">
              <div className="space-y-6">
                <FooterLinkComp title="About Us" links={aboutLinks} />
                <FooterLinkComp title="Company Information" links={companyInfo} />
              </div>
              <div>
                <FooterLinkComp title="Categories" links={categoryFooterLinks} />
              </div>
              <div className="space-y-6">
                <FooterLinkComp title="Other Information" links={otherLinks} />
                <FooterLinkComp title="FAQs" links={faqLinks} />
                {/* Contact details component */}
                <ContactDetails />
              </div>
            </div>

            {/* Mobile version */}
            <div className="block md:hidden">
              <div>
                <div className="flex flex-wrap gap-2 items-start">
                  <div className="flex-col gap-y-2">
                    <FooterLinkComp title="About Us" links={aboutLinks} />
                    <FooterLinkComp title="Company Information" links={companyInfo} />
                    <FooterLinkComp title="FAQs" links={faqLinks} />
                  </div>
                  <div className="flex-col gap-y-2">
                    <FooterLinkComp title="Categories" links={categoryFooterLinks} />
                    <FooterLinkComp title="Other Information" links={otherLinks} />
                  </div>
                </div>
              </div>
              <div>
                <ContactDetails />
              </div>
            </div>
            <div className="block md:hidden">
              <div className="space-y-4">
                <h3 className="text-lg text-foreground font-semibold">Follow Us</h3>
                <div className="flex items-center gap-5">
                  {socialLinks.map((link) => {
                    const IconTag = link.icon;
                    return (
                      <Link
                        to={link.pathname}
                        target="_blank"
                        key={link.id}
                        aria-label={`Follow us on ${link?.name}`}
                      >
                        <IconTag className="text-primary text-2xl" />
                      </Link>
                    );
                  })}
                </div>
              </div>
              <Divider className="my-2" />
              <p className="text-xs font-normal text-gray dark:text-gray-100 text-center">
                Copyright {new Date().getFullYear()} Promotional Products Now
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
